import { instance } from '@sas-dvr/ltjs-sasicu/util/SASFormatterUtil';
import { jsDateToSASDate, jsDateToSASDateTime, sasDateTimeToJsDate, sasDateToJsDate, sasTimeToJsDate, JSDATE_FOR_MAX_SASDATE, JSDATE_FOR_MAX_SASDATETIME, JSDATE_FOR_MIN_SASDATE, JSDATE_FOR_MIN_SASDATETIME, } from '@sas-dvr/ltjs-sasicu/util/CalendarUtil';
const minSasDate = jsDateToSASDate(JSDATE_FOR_MIN_SASDATE);
const maxSasDate = jsDateToSASDate(JSDATE_FOR_MAX_SASDATE);
const minSasDateTime = jsDateToSASDateTime(JSDATE_FOR_MIN_SASDATETIME);
const maxSasDateTime = jsDateToSASDateTime(JSDATE_FOR_MAX_SASDATETIME);
export function clampValue(value, format) {
    if (format.isDateFormat()) {
        return Math.min(maxSasDate, Math.max(minSasDate, value));
    }
    if (format.isDateTimeFormat()) {
        return Math.min(maxSasDateTime, Math.max(minSasDateTime, value));
    }
    if (!Number.isSafeInteger(Math.ceil(value))) {
        return Math.min(Number.MAX_SAFE_INTEGER, Math.max(Number.MIN_SAFE_INTEGER, value));
    }
    return value;
}
export function roundDateForFormat(value, isDate, format) {
    const date = isDate ? sasDateToJsDate(value) : sasDateTimeToJsDate(value);
    const jsValue = new Date(calculateValueForFormat(date.valueOf(), format));
    return isDate ? jsDateToSASDate(jsValue) : jsDateToSASDateTime(jsValue);
}
/**
 * This doc outlines VA's expectations: https://sww.sas.com/saspedia/Visual_Analytics_Next_Range_Slider_Requirements
 *
 * Forked from:
 * https://gitlab.sas.com/convoy/visual-analytics-app/-/blob/master/ui5/src/main/openuilibs/sas.van/src/sas/van/controls/SliderUtil.ts?ref_type=heads
 *
 */
export function roundRangeForFormat(minBound, maxBound, format) {
    const formatter = instance(format);
    if ((formatter === null || formatter === void 0 ? void 0 : formatter.isDateFormat()) || (formatter === null || formatter === void 0 ? void 0 : formatter.isTimeFormat()) || (formatter === null || formatter === void 0 ? void 0 : formatter.isDateTimeFormat())) {
        // todo: one specical case?
        const isDate = formatter === null || formatter === void 0 ? void 0 : formatter.isDateFormat();
        return {
            min: roundDateForFormat(minBound, isDate, format),
            max: roundDateForFormat(maxBound, isDate, format),
        };
    }
    const stepWidth = calculateFormatStepWidth(format, minBound, maxBound);
    return {
        min: roundMinValue(minBound, stepWidth, format),
        max: roundMaxValue(maxBound, stepWidth, format),
    };
}
function formatParts(format) {
    const name = format.toUpperCase().split(/\d/)[0];
    const size = format.substring(name.length).split('.');
    return {
        name,
        width: parseInt(size[0]),
        decimal: parseInt(size[1]),
    };
}
/**
 * Returns true if the format is a Year, Week format and false otherwise. This is used to determine
 * if a slider should use the week picker.
 */
export function isYearWeek(name, width) {
    if (name === 'DATEYW' || name === 'DTDATEYW') {
        return true;
    }
    // For WEEKV only widths of 5 and 6 are YEAR Week formats
    if (name === 'WEEKV' && (width === 5 || width === 6)) {
        return true;
    }
    return false;
}
/**
 * Calculate valid value based on format.
 *
 * Certain date/time formats need to adjust the value to a valid value based on the format.
 * For example, a format of MONYY should return a value corresponding to the first day of the month.
 * If a value is set that is another day in the month, adjust the value to represent the first day of that month.
 *
 * https://gitlab.sas.com/htmlcommons/openui-framework/-/blob/master/src/main/openuilibs/sas.hc.m/src/sas/hc/m/slider/_formatUtil.js?ref_type=heads#L43
 *
 * @private
 */
export function calculateValueForFormat(value, format) {
    if (!format) {
        return value;
    }
    let finalFormat = format;
    const { name, decimal, width } = formatParts(format);
    switch (name) {
        case 'DATE':
        case 'DATEN':
        case 'DATEYW':
        case 'DAY':
        case 'DTDATE':
        case 'DDMMYY':
        case 'DOWNAME':
        case 'DTWKDATX':
        case 'HDATE':
        case 'HEBDATE':
        case 'JULDAY':
        case 'JULIAN':
        case 'MMDDYY':
        case 'WEEKDATE':
        case 'WORDDATE':
        case 'YYMMDD':
            finalFormat = 'DTDATE10';
            break;
        case 'DATETIME':
            finalFormat = 'DATETIME18';
            break;
        case 'DTMONYY':
        case 'MMYY':
        case 'MONYY':
        case 'YYMM':
        case 'MONNAME':
            finalFormat = 'MONYY7';
            break;
        case 'DTYYQC':
        case 'QTR':
        case 'YYQC':
            finalFormat = 'YYQC8';
            break;
        case 'YEAR':
        case 'DTYEAR': {
            const date = new Date(value);
            date.setDate(1);
            date.setMonth(0);
            value = date.valueOf();
            finalFormat = 'DTDATE10';
            break;
        }
        case 'WEEKV': {
            finalFormat = 'DTDATE10';
            if (!isYearWeek(name, width)) {
                break;
            }
            const date = new Date(value);
            // find the Sunday at the beginning of the week and
            // add 1 because weeks start on Monday
            date.setDate(date.getDate() - date.getDay() + 1);
            value = date.valueOf();
            break;
        }
    }
    // If we have a decimal ignore our previous switch statement?
    // Does this actually have any function?
    if (decimal > 0) {
        // finalFormat = formatDecimals[0].concat('.' + iFormatDecimal);
        finalFormat = format;
    }
    const formatter = instance(finalFormat);
    if (!formatter) {
        return value;
    }
    let parsedValue;
    if (formatter.isDateFormat() === true) {
        const sasDate = jsDateToSASDate(new Date(value));
        const formattedValue = formatter.format(sasDate);
        const parserValue = formatter.parse(formattedValue);
        if (!isNaN(parserValue)) {
            parsedValue = sasDateToJsDate(parserValue);
        }
    }
    else if (formatter.isDateTimeFormat() === true) {
        const sasDateTime = jsDateToSASDateTime(new Date(value));
        const formattedValue = formatter.format(sasDateTime);
        const parserValue = formatter.parse(formattedValue);
        if (!isNaN(parserValue)) {
            // TOD is parsed as a time value even though it's technically a datetime
            if (name === 'TOD') {
                parsedValue = sasTimeToJsDate(parserValue);
                // I assume this was a typo? iParsedJSValue is undefined here.
                // iParsedJSValue = CalendarUtil.sasTimeToJsDate(iParsedJSValue);
            }
            else {
                parsedValue = sasDateTimeToJsDate(parserValue);
            }
        }
    }
    if (parsedValue) {
        const value = parsedValue.valueOf();
        if (!isNaN(value)) {
            return value;
        }
    }
    return value;
}
/**
 * Forked from:
 * https://gitlab.sas.com/htmlcommons/openui-framework/-/blob/master/src/main/openuilibs/sas.hc.m/src/sas/hc/m/slider/_calculationUtil.js?ref_type=heads#L111
 */
function roundMinValue(value, stepWidth, format) {
    const stepWidthValue = getSmallStepWidthValue(value, stepWidth);
    if (!format) {
        return stepWidthValue;
    }
    if (stepWidthValue > value) {
        return getSmallStepWidthValue(value - stepWidth, stepWidth);
    }
    return stepWidthValue;
}
function roundMaxValue(value, stepWidth, format) {
    const stepWidthValue = getSmallStepWidthValue(value, stepWidth);
    if (!format) {
        return stepWidthValue;
    }
    if (stepWidthValue < value) {
        return getSmallStepWidthValue(value + stepWidth, stepWidth);
    }
    return stepWidthValue;
}
function getSmallStepWidthValue(value, stepWidth) {
    if (stepWidth === null || stepWidth === undefined || stepWidth === 0) {
        return value;
    }
    const index = String(stepWidth).indexOf('.');
    let precision = 0;
    if (index >= 0) {
        precision = String(stepWidth).length - index - 1;
    }
    if (stepWidth === 1) {
        return Math.round(value);
    }
    // keep the type coercion of the previous openui code.
    const fDivValue = (value / stepWidth).toFixed(precision);
    const iDivFloor = Math.floor(fDivValue);
    // can't use % operator because it has double precision problems
    // for example 1 % .01 is not 0
    const iMod = (fDivValue - iDivFloor) * stepWidth;
    const fNumForRound = stepWidth / 2;
    let fAdjustedValue = iDivFloor * stepWidth;
    if (iMod >= fNumForRound) {
        fAdjustedValue = fAdjustedValue + stepWidth;
    }
    return parseFloat(fAdjustedValue.toFixed(precision));
}
function calculateFormatStepWidth(format, min, max) {
    let stepWidth = 1;
    if (!format) {
        return 1;
    }
    // const aResults=format.match(/\d+/g);
    const { name, decimal } = formatParts(format);
    const width = decimal;
    if (name === 'BEST') {
        return calculateFormatStepWidthForBest(min, max);
    }
    // Set the precision
    // iPrecision = parseInt(aResults[1]);
    let i = 0;
    for (i; i < width; i++) {
        stepWidth = stepWidth / 10;
    }
    stepWidth = parseFloat(stepWidth.toFixed(width));
    // now divide by 100 for PERCENT because it multiplies the value by 100
    if (name === 'PERCENT') {
        stepWidth = stepWidth / 100;
    }
    return stepWidth;
}
function calculateFormatStepWidthForBest(min, max) {
    const range = Math.abs(max - min);
    let stepWidth = 1;
    let steps = range / stepWidth;
    while (steps < 10) {
        stepWidth = stepWidth / 10;
        steps = range / stepWidth;
    }
    return stepWidth;
}
